/**
 * Tab Utenti nell'Admin Panel.
 * CRUD completo per la gestione utenti.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { authApi, User, RegisterRequest, UserUpdateRequest } from '../../api/auth'
import { vetsApi, Vet } from '../../api/vets'
import { SkeletonList } from '../../components/Skeleton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { UserPlus, Pencil, Trash2, Power, PowerOff } from 'lucide-react'

const ROLES = [
  { value: 'vet', label: 'Veterinario' },
  { value: 'admin', label: 'Admin' },
  { value: 'staff', label: 'Staff' },
]

export function UsersTab() {
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [deletingUser, setDeletingUser] = useState<User | null>(null)

  const { data: users, isLoading } = useQuery<User[]>({
    queryKey: ['users'],
    queryFn: () => authApi.listUsers(0, 200),
  })

  const { data: vets } = useQuery<Vet[]>({
    queryKey: ['vets'],
    queryFn: () => vetsApi.list(),
  })

  const createMutation = useMutation({
    mutationFn: (data: RegisterRequest) => authApi.register(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowCreate(false)
      resetCreateForm()
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante la creazione')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UserUpdateRequest }) =>
      authApi.updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditingUser(null)
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante l\'aggiornamento')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (userId: string) => authApi.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setDeletingUser(null)
    },
    onError: (error: any) => {
      alert(error?.response?.data?.detail || 'Errore durante l\'eliminazione')
    },
  })

  const [createForm, setCreateForm] = useState<RegisterRequest>({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'vet',
    vet_id: null,
  })

  const [editForm, setEditForm] = useState<UserUpdateRequest>({})

  const resetCreateForm = () => {
    setCreateForm({ username: '', email: '', password: '', full_name: '', role: 'vet', vet_id: null })
  }

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.username.trim() || !createForm.email.trim() || !createForm.password || !createForm.full_name.trim()) {
      alert('Compila tutti i campi obbligatori')
      return
    }
    if (createForm.password.length < 8) {
      alert('La password deve avere almeno 8 caratteri')
      return
    }
    createMutation.mutate(createForm)
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    setEditForm({
      email: user.email,
      full_name: user.full_name,
      role: user.role,
      is_active: user.is_active,
      vet_id: user.vet_id,
    })
  }

  const handleSaveEdit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return
    if (editForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editForm.email)) {
      alert('Email non valida')
      return
    }
    updateMutation.mutate({ userId: editingUser.id, data: editForm })
  }

  const handleDelete = () => {
    if (!deletingUser) return
    deleteMutation.mutate(deletingUser.id)
  }

  const handleToggleActive = (user: User) => {
    const currentUser = authApi.getUser()
    if (user.id === currentUser?.id) {
      alert('Non puoi disattivare il tuo account')
      return
    }
    updateMutation.mutate({ userId: user.id, data: { is_active: !user.is_active } })
  }

  const getVetName = (vetId: string | null) => {
    if (!vetId) return 'Nessuno'
    const vet = vets?.find(v => v.id === vetId)
    return vet ? vet.full_name : 'N/A'
  }

  if (isLoading) return <SkeletonList count={5} />

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-foreground">Gestione Utenti</h2>
        <Button onClick={() => setShowCreate(true)}>
          <UserPlus className="size-4 mr-2" />
          Aggiungi Utente
        </Button>
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Username</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Nome</TableHead>
              <TableHead>Ruolo</TableHead>
              <TableHead>Veterinario</TableHead>
              <TableHead>Stato</TableHead>
              <TableHead>Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users?.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.username}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.full_name}</TableCell>
                <TableCell>
                  <Badge variant={user.role === 'admin' ? 'destructive' : user.role === 'staff' ? 'secondary' : 'default'} className="text-xs">
                    {user.role}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{getVetName(user.vet_id)}</TableCell>
                <TableCell>
                  <Badge variant="outline" className={
                    user.is_active
                      ? 'bg-success/10 text-success border-success/20'
                      : 'bg-muted text-muted-foreground border-border'
                  }>
                    {user.is_active ? 'Attivo' : 'Disattivato'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="size-8" onClick={() => handleEdit(user)} title="Modifica">
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8"
                      onClick={() => handleToggleActive(user)}
                      title={user.is_active ? 'Disattiva' : 'Riattiva'}
                    >
                      {user.is_active ? <PowerOff className="size-3.5" /> : <Power className="size-3.5" />}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-8 text-destructive hover:text-destructive"
                      onClick={() => setDeletingUser(user)}
                      title="Elimina"
                    >
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Utente</DialogTitle>
            <DialogDescription>Crea un nuovo utente per il sistema.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label>Username *</Label>
              <Input
                value={createForm.username}
                onChange={(e) => setCreateForm({ ...createForm, username: e.target.value.replace(/[^a-zA-Z0-9_]/g, '') })}
                placeholder="nome_utente"
                required
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                placeholder="email@example.com"
                required
              />
            </div>
            <div>
              <Label>Password * (min 8 caratteri)</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                placeholder="••••••••"
                required
                minLength={8}
              />
            </div>
            <div>
              <Label>Nome completo *</Label>
              <Input
                value={createForm.full_name}
                onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })}
                placeholder="Dr. Mario Rossi"
                required
              />
            </div>
            <div>
              <Label>Ruolo</Label>
              <Select
                value={createForm.role}
                onValueChange={(value) => setCreateForm({ ...createForm, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assegna a Veterinario (opzionale)</Label>
              <Select
                value={createForm.vet_id || 'none'}
                onValueChange={(value) => setCreateForm({ ...createForm, vet_id: value === 'none' ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Nessun veterinario" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun veterinario</SelectItem>
                  {vets?.filter(v => v.is_active).map(vet => (
                    <SelectItem key={vet.id} value={vet.id}>{vet.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Annulla</Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creazione...' : 'Crea Utente'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Utente</DialogTitle>
            <DialogDescription>Modifica i dati dell'utente {editingUser?.username}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <div>
              <Label>Username</Label>
              <Input value={editingUser?.username || ''} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground mt-1">Lo username non può essere modificato</p>
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={editForm.email || ''}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Nome completo</Label>
              <Input
                value={editForm.full_name || ''}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Ruolo</Label>
              <Select
                value={editForm.role || 'vet'}
                onValueChange={(value) => setEditForm({ ...editForm, role: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assegna a Veterinario</Label>
              <Select
                value={editForm.vet_id || 'none'}
                onValueChange={(value) => setEditForm({ ...editForm, vet_id: value === 'none' ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Nessun veterinario" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nessun veterinario</SelectItem>
                  {vets?.filter(v => v.is_active).map(vet => (
                    <SelectItem key={vet.id} value={vet.id}>{vet.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Stato</Label>
              <Select
                value={editForm.is_active === undefined ? 'true' : String(editForm.is_active)}
                onValueChange={(value) => setEditForm({ ...editForm, is_active: value === 'true' })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Attivo</SelectItem>
                  <SelectItem value="false">Disattivato</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditingUser(null)}>Annulla</Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? 'Salvataggio...' : 'Salva Modifiche'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingUser} onOpenChange={(open) => !open && setDeletingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina Utente</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare l'utente <strong>{deletingUser?.username}</strong>?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingUser(null)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
